import React from "react";
import { PropTypes } from "prop-types";

import { apiClient } from "../../utils/apiClient";
import playerLoader from "../../utils/playerLoader";
import style from "./PlayerShadowDom.module.scss";
import { GetAssetFolderURL } from "../../UseCases/HostHandlers/GetAssetFolderURL";
import { CloseAssetSystemPlugin } from "../../UseCases/HostHandlers/CloseAssetSystemPlugin";
import assetSystemPlugin from "../../Data/assetSystemPlugin.json";
import { makeHostDispatcher } from "@vived/vivedlearning_host";
import PageError from "../Error/PageError";

class NewModelShadowDom extends React.Component {
  playerContainer = null;
  app = null;
  unmounted = false;

  constructor(props) {
    super(props);
    this.state = {
      styles: [],
      error: false,
    };
  }

  async componentDidMount() {
    try {
      // The entrypoints come from the API, which returns absolute, signed
      // CloudFront URLs. The raw apps bucket is no longer publicly readable,
      // so it cannot be fetched from directly. OUZ-187
      const { data } = await apiClient({
        method: "get",
        path: `apps/${assetSystemPlugin.id}/${assetSystemPlugin.version}`,
        query: { function_version: "2" },
        userToken: this.props.userToken,
      });
      if (this.unmounted) return;

      const entrypoints = data && data.app ? data.app.files : null;
      if (!entrypoints || entrypoints.length === 0) {
        throw new Error(
          `No entrypoints for app ${assetSystemPlugin.id} ${assetSystemPlugin.version}`
        );
      }

      const shadow = this.buildShadow();

      const styles = await playerLoader(entrypoints, shadow);
      if (this.unmounted) return;

      this.setState({
        styles,
      });

      this.startPlayer(shadow);
    } catch (e) {
      // Without this the rejection escapes componentDidMount as an unhandled
      // promise and the user is left looking at an empty container. OUZ-182
      console.error("[NewModelShadowDom] failed to load the plugin: ", e);
      if (!this.unmounted) {
        this.setState({
          error: true,
        });
      }
    }
  }

  componentWillUnmount() {
    this.unmounted = true;
  }

  buildShadow() {
    let shadowParent = document.getElementById("asset-system-plugin-shadow");
    let shadow = shadowParent.attachShadow({ mode: "open" });

    shadow.innerHTML = `
    <link type="text/css" href="https://fonts.googleapis.com/icon?family=Material+Icons" rel="stylesheet">
    <style> .player-container{ display: flex; position: relative; height: 100%; width: 100%; } </style>
    <div id="asset-system-plugin-container" class="player-container" />
    `;

    this.playerContainer = shadow.getElementById(
      "asset-system-plugin-container"
    );

    shadow.createElement = (...args) => document.createElement(...args);
    shadow.createElementNS = (...args) => document.createElementNS(...args);
    shadow.createTextNode = (...args) => document.createTextNode(...args);

    return shadow;
  }

  startPlayer() {
    try {
      const appWithVersion =
        assetSystemPlugin.id +
        "-" +
        assetSystemPlugin.version.replaceAll(".", "_");
      this.app = window[appWithVersion];

      const hostHandler = this.props.hostHandler;
      const dispatchType = this.props.dispatchType;

      new GetAssetFolderURL(hostHandler);
      new CloseAssetSystemPlugin(hostHandler, this.props.history);

      const appHandler = this.app.mount(hostHandler.handler);
      const hostDispatcher = makeHostDispatcher();
      hostDispatcher.registerAppHandler(appHandler);

      const payload = {
        channelID: this.props.channelId,
        channelName: this.props.channelName,
        userToken: this.props.userToken,
        container: this.playerContainer,
        baseApiUrl: "https://api.vivedlearning.com",
      };

      hostDispatcher.dispatch({
        version: 1,
        type: dispatchType,
        payload,
      });
    } catch (e) {
      console.log("[NewModelShadowDom] error caught: ", e);
    }
  }

  render() {
    if (this.state.error) {
      return (
        <PageError
          error={true}
          title="Unable to load the asset system"
          errorMessage="Something went wrong while loading the asset system. Please reload the page and try again."
        />
      );
    }

    let styles = [];

    this.state.styles.forEach((style) => {
      styles.push(style);
    });

    const styles2 = [
      style.container,
      this.props.size === "normal" ? style.containerNormal : null,
      this.props.size === "freeplay" ? style.containerFreeplayFullsize : null,
      this.props.size === "authoring" ? style.containerAuthoring : null,
      this.props.size === "embed" ? style.containerEmbed : null,
      this.props.size === "fill" ? style.containerFill : null,
    ];

    styles2.forEach((style) => {
      styles.push(style);
    });

    const classes = styles.join(" ");

    return <div id="asset-system-plugin-shadow" className={classes} />;
  }
}

NewModelShadowDom.defaultProps = {
  appBar: false,
};

NewModelShadowDom.propTypes = {
  appBar: PropTypes.bool,
  size: PropTypes.oneOf(["normal", "freeplay", "authoring", "embed", "fill"])
    .isRequired,
  title: PropTypes.string,
  channelId: PropTypes.string.isRequired,
  channelName: PropTypes.string.isRequired,
  userToken: PropTypes.string.isRequired,
  history: PropTypes.object.isRequired,
  hostHandler: PropTypes.object.isRequired,
  dispatchType: PropTypes.string.isRequired,
};

export default NewModelShadowDom;
